// Bank Connection Profiles console (admin-portal, PayGate D18): the platform surface
// for configuring a tenant's per-bank SFTP host-to-host endpoints — host/port/user,
// SSH key or password + pinned host key, upload/return directories, file format, and
// PGP. Operators can add/edit a profile, test the connection (dials + authenticates,
// no payment is sent), enable/disable it, and delete it. When an SFTP profile is
// enabled the payout connector uses it (preferred over the tenant's provider config).
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Button, Card, Form, Input, InputNumber, Modal, Select, Space, Switch, Table, Tag, Typography, message,
} from 'antd';
import { ReloadOutlined, PlusOutlined, ThunderboltOutlined, DeleteOutlined } from '@ant-design/icons';
import * as api from '../../services/connectionProfilesService';

const { Title, Text, Paragraph } = Typography;
const COUNTRIES = ['TZ', 'KE', 'UG', 'RW', 'ZA', 'US'];
const errMsg = (e: any, fb: string) =>
  e?.response?.data?.error?.message || e?.response?.data?.message || e?.message || fb;

export const ConnectionProfilesPage: React.FC = () => {
  const [tenantId, setTenantId] = useState('');
  const [profiles, setProfiles] = useState<api.Profile[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<api.Profile | null>(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [form] = Form.useForm();

  const load = useCallback(async (tid: string) => {
    if (!tid) return;
    setLoading(true);
    try {
      setProfiles(await api.listProfiles(tid.trim()));
    } catch (e) {
      message.error(errMsg(e, 'Could not load profiles'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // hydrate from ?tenantId if present (deep-link from a tenant page)
    const p = new URLSearchParams(window.location.search).get('tenantId');
    if (p) {
      setTenantId(p);
      load(p);
    }
  }, [load]);

  const openNew = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      connection_type: 'sftp', environment: 'sandbox', file_format: 'csv', port: 22,
      priority: 100, pgp_required: false, manual_fallback_enabled: true, enabled: false,
      insecure_ignore_host_key: false,
    });
    setOpen(true);
  };

  const openEdit = (p: api.Profile) => {
    setEditing(p);
    form.resetFields();
    form.setFieldsValue({ ...p, insecure_ignore_host_key: false }); // secrets never returned — left blank
    setOpen(true);
  };

  const save = async () => {
    const v = await form.validateFields();
    setSaving(true);
    try {
      const secrets: api.Secrets = {
        password: v.password || undefined,
        private_key: v.private_key || undefined,
        passphrase: v.passphrase || undefined,
        host_key: v.host_key || undefined,
        insecure_ignore_host_key: !!v.insecure_ignore_host_key,
        pgp_public_key: v.pgp_public_key || undefined,
      };
      const anySecret = Object.values(secrets).some((x) => x !== undefined && x !== false);
      const {
        password, private_key, passphrase, host_key, pgp_public_key, insecure_ignore_host_key, ...rest
      } = v;
      await api.saveProfile(tenantId.trim(), { ...rest, secrets: anySecret ? secrets : null }, editing?.id);
      message.success('Profile saved');
      setOpen(false);
      await load(tenantId);
    } catch (e: any) {
      if (e?.errorFields) return; // form validation
      message.error(errMsg(e, 'Could not save profile'));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (p: api.Profile, enabled: boolean) => {
    try {
      await api.setProfileEnabled(tenantId.trim(), p.id, enabled);
      setProfiles((ps) => ps.map((x) => (x.id === p.id ? { ...x, enabled } : x)));
    } catch (e) {
      message.error(errMsg(e, 'Could not update'));
    }
  };

  const test = async (p: api.Profile) => {
    setTestingId(p.id);
    try {
      const r = await api.testProfile(tenantId.trim(), p.id);
      if (r.ok) message.success(`${p.bank_code}: ${r.message || 'connected'}`);
      else message.error(`${p.bank_code}: ${r.error || 'connection failed'}`, 6);
    } catch (e) {
      message.error(errMsg(e, 'Test failed'));
    } finally {
      setTestingId(null);
    }
  };

  const remove = (p: api.Profile) => {
    Modal.confirm({
      title: `Delete the ${p.bank_code} (${p.environment}) profile?`,
      okType: 'danger',
      onOk: async () => {
        try {
          await api.deleteProfile(tenantId.trim(), p.id);
          await load(tenantId);
        } catch (e) {
          message.error(errMsg(e, 'Could not delete'));
        }
      },
    });
  };

  const columns = [
    { title: 'Bank', dataIndex: 'bank_code', render: (v: string, r: api.Profile) => <Space><b>{v}</b><Tag>{r.country}</Tag></Space> },
    { title: 'Type', dataIndex: 'connection_type', render: (v: string) => <Tag color="blue">{v}</Tag> },
    { title: 'Env', dataIndex: 'environment', render: (v: string) => <Tag color={v === 'live' ? 'red' : 'default'}>{v}</Tag> },
    { title: 'Host', dataIndex: 'host', render: (v: string, r: api.Profile) => (v ? `${v}:${r.port}` : '—') },
    { title: 'Format', dataIndex: 'file_format' },
    { title: 'PGP', dataIndex: 'pgp_required', render: (v: boolean) => (v ? <Tag color="purple">PGP</Tag> : '—') },
    { title: 'Creds', dataIndex: 'has_credentials', render: (v: boolean) => (v ? <Tag color="green">set</Tag> : <Tag color="orange">none</Tag>) },
    { title: 'Enabled', dataIndex: 'enabled', render: (v: boolean, r: api.Profile) => <Switch checked={v} onChange={(c) => toggle(r, c)} /> },
    {
      title: 'Actions',
      render: (_: unknown, r: api.Profile) => (
        <Space>
          <Button size="small" onClick={() => openEdit(r)}>Edit</Button>
          <Button size="small" icon={<ThunderboltOutlined />} loading={testingId === r.id} onClick={() => test(r)}>Test</Button>
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => remove(r)} />
        </Space>
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 1200 }}>
      <Title level={3}>Bank Connection Profiles</Title>
      <Paragraph type="secondary">
        Per-tenant, per-bank SFTP host-to-host endpoints. When an SFTP profile is enabled, PayGate uses it to
        transmit that tenant's approved payroll to the bank. Credentials (SSH key, passphrase, host key, PGP)
        are encrypted at rest and never shown again — leave blank when editing to keep them.
      </Paragraph>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Text strong>Tenant ID</Text>
          <Input
            style={{ width: 340 }}
            placeholder="tenant UUID"
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            onPressEnter={() => load(tenantId)}
          />
          <Button icon={<ReloadOutlined />} onClick={() => load(tenantId)} disabled={!tenantId}>Load</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openNew} disabled={!tenantId}>Add profile</Button>
        </Space>
      </Card>

      {tenantId ? (
        <Card size="small">
          <Table rowKey="id" size="small" loading={loading} columns={columns as any} dataSource={profiles} pagination={false} />
        </Card>
      ) : (
        <Alert type="info" showIcon message="Enter a tenant ID to view and manage its bank connection profiles." />
      )}

      <Modal
        title={editing ? `Edit ${editing.bank_code} profile` : 'Add bank connection profile'}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={save}
        confirmLoading={saving}
        okText="Save"
        width={640}
      >
        <Form form={form} layout="vertical" size="small">
          <Space wrap size="middle">
            <Form.Item name="bank_code" label="Bank code" rules={[{ required: true }]}><Input placeholder="NMB" style={{ width: 160 }} disabled={!!editing} /></Form.Item>
            <Form.Item name="country" label="Country" rules={[{ required: true }]}><Select options={COUNTRIES.map((c) => ({ value: c, label: c }))} style={{ width: 120 }} /></Form.Item>
            <Form.Item name="connection_type" label="Type"><Select options={['sftp', 'api', 'gateway', 'manual'].map((v) => ({ value: v, label: v }))} style={{ width: 120 }} /></Form.Item>
            <Form.Item name="environment" label="Environment" rules={[{ required: true }]}><Select options={['sandbox', 'live'].map((v) => ({ value: v, label: v }))} style={{ width: 120 }} disabled={!!editing} /></Form.Item>
            <Form.Item name="priority" label="Priority"><InputNumber min={1} /></Form.Item>
          </Space>
          <Space wrap size="middle">
            <Form.Item name="host" label="SFTP host"><Input placeholder="sftp.bank.co.tz" style={{ width: 240 }} /></Form.Item>
            <Form.Item name="port" label="Port"><InputNumber min={1} max={65535} /></Form.Item>
            <Form.Item name="username" label="Username"><Input style={{ width: 160 }} /></Form.Item>
          </Space>
          <Space wrap size="middle">
            <Form.Item name="remote_upload_dir" label="Upload dir"><Input placeholder="/inbound" style={{ width: 200 }} /></Form.Item>
            <Form.Item name="remote_return_dir" label="Return dir"><Input placeholder="/outbound" style={{ width: 200 }} /></Form.Item>
            <Form.Item name="file_naming_pattern" label="File name"><Input placeholder="KP_{ts}.{ext}" style={{ width: 200 }} /></Form.Item>
          </Space>
          <Space wrap size="middle">
            <Form.Item name="file_format" label="File format"><Select options={['csv', 'xlsx'].map((v) => ({ value: v, label: v }))} style={{ width: 120 }} /></Form.Item>
            <Form.Item name="pgp_required" label="PGP encrypt" valuePropName="checked"><Switch /></Form.Item>
            <Form.Item name="manual_fallback_enabled" label="Manual fallback" valuePropName="checked"><Switch /></Form.Item>
            <Form.Item name="enabled" label="Enabled" valuePropName="checked"><Switch /></Form.Item>
          </Space>

          <Alert type="warning" showIcon style={{ margin: '8px 0' }}
            message="Credentials (sealed — leave blank to keep existing)" />
          <Space wrap size="middle">
            <Form.Item name="password" label="Password"><Input.Password autoComplete="new-password" style={{ width: 220 }} /></Form.Item>
            <Form.Item name="passphrase" label="Key passphrase"><Input.Password autoComplete="new-password" style={{ width: 220 }} /></Form.Item>
          </Space>
          <Form.Item name="private_key" label="SSH private key (PEM)"><Input.TextArea rows={3} /></Form.Item>
          <Form.Item name="host_key" label="Server host key (authorized-keys line — required for live)"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="pgp_public_key" label="Bank PGP public key (if encryption required)"><Input.TextArea rows={3} /></Form.Item>
          <Form.Item name="insecure_ignore_host_key" label="Skip host-key check (sandbox only — never for live)" valuePropName="checked"><Switch /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ConnectionProfilesPage;
